import React, { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { Listbox } from "@headlessui/react";
import mailIcon from "../../icons/popup/email.svg";
import { lookupQueries } from "@surgia/api/src/queryStore/shared/lookup";
import { requestQueries } from "@surgia/api/src/queryStore/admin/request";
import sortIcon from "@surgia/ui/icons/table/sort.svg";
import { countries } from "../../../../contract/constants/countryCodes";
import {
  createBranchSchema,
  defaultValues,
  BranchFormValues,
} from "./validation";
import { useQuery } from "@tanstack/react-query";
import FormInput from "../../components/inputs/FormInput";
import Button from "../../components/button";
import { tenantQueries } from "@surgia/api/src/queryStore/admin/tenant";
import { toast } from "sonner";

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="ml-auto size-4 opacity-100"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5"></path>
  </svg>
);

export const AssignBranchForm: React.FC<{
  onClose: () => void;
  type?: "create" | "edit";
  requestId?: number;
  branchId?: number;
  onSuccess?: () => void;
  createTenantBranch?: boolean;
  createBranch?: boolean;
  countryId: number;
}> = ({
  onClose,
  type = "create",
  requestId,
  branchId,
  onSuccess,
  createTenantBranch,
  createBranch,
  countryId,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [showDropdown, setShowDropdown] = useState(false);
  const [phoneFocused, setPhoneFocused] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Add mutations
  const createBranchMutation = useMutation({
    mutationFn: (payload: any) =>
      requestQueries.mutations
        .createBranch(requestId || 0, payload)
        .mutationFn(),
  });

  const updateBranchMutation = useMutation({
    mutationFn: (payload: any) =>
      requestQueries.mutations
        .updateBranch(requestId || 0, branchId || 0, payload)
        .mutationFn(),
  });

  // Add the createTenantBranch mutation
  const createTenantBranchMutation = useMutation({
    mutationFn: (payload: any) =>
      tenantQueries.mutations
        .createTenantBranch(
          requestId || 0,
          import.meta.env.VITE_PUBLIC_COMPONENT_ID
        )
        .mutationFn(payload),
    onSuccess: () => {
      toast.success(t("admin.toasters.branchCreated"), {});

      // Call the parent's onSuccess callback with the data
      // This allows the parent component to update the global state
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.errorCodeDescription ||
        "An error occurred";
      if (errorMessage.includes("name")) {
        toast.error(t("hub.register.branchNameExists"));
      } else if (errorMessage.includes("email")) {
        toast.error(t("hub.register.emailExists"));
      } else if (errorMessage.includes("mobile")) {
        toast.error(t("hub.register.mobileExists"));
      }
    },
  });

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    setError,
    clearErrors, // Add this to clear errors
    formState: { errors, isSubmitting },
  } = useForm<BranchFormValues>({
    resolver: zodResolver(createBranchSchema(t)),
    defaultValues,
    mode: "onChange",
  });

  // Watch city to fetch areas
  const selectedCity = watch("city");
  const selectedPhone = watch("phone");
  const selectedCountryCode = watch("country"); // Watch the country code
  const selectedCountry =
    countries.find((c: any) => c.dial === selectedCountryCode) || countries[1];
  // Fetch cities
  const { data: citiesData } = useQuery(
    lookupQueries.queries.cities({ size: 1000, country_id: countryId })
  );

  // Fetch areas only when city is selected
  const { data: areasData } = useQuery({
    ...lookupQueries.queries.areas({ city_id: parseInt(selectedCity) }),
    enabled: !!selectedCity,
  });

  const [previousCity, setPreviousCity] = useState(selectedCity);

  useEffect(() => {
    // Only reset if city changed AND there was a previous city
    if (selectedCity && selectedCity !== previousCity && previousCity) {
      setValue("district", "");
    }
    setPreviousCity(selectedCity);
  }, [selectedCity, setValue, previousCity]);

  const onSelectCountry = (code: string) => {
    setValue("country", code);
    setShowDropdown(false);
  };

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);
  const onSubmit = (data: BranchFormValues) => {
    // Manual phone validation
    const country = countries.find((c: any) => c.code === data.country);
    if (
      (country &&
        !country.regex.test(data?.phone || "") &&
        data?.phone?.length) ??
      0 > 0
    ) {
      setError("phone", {
        type: "manual",
        message: t("shared.branchForm.invalidPhone"),
      });
      return;
    }

    // Prepare payload
    const addressPayload: any = {
      country_id: countryId,
      city_id: parseInt(data.city),
      street: data.address,
    };
    
    // Only include area_id if district is provided
    if (data.district) {
      addressPayload.area_id = parseInt(data.district);
    }
    
    const payload = {
      branch_name: data.branchName,
      address: addressPayload,
      contact_person_first_name: data.contactFirstName,
      contact_person_last_name: data.contactLastName,
      contact_person_mobile: `${selectedCountry.dial}${data.phone}`,
      contact_person_email: data.contactEmail,
    };

    // Call appropriate mutation based on type and createTenantBranch flag
    if (createTenantBranch) {
      createTenantBranchMutation.mutate(payload, {
        onSuccess: () => {
          onSuccess?.();
        },
      });
    } else if (type === "create") {
      createBranchMutation.mutate(payload, {
        onSuccess: () => {
          onSuccess?.();
        },
      });
    } else {
      updateBranchMutation.mutate(payload, {
        onSuccess: () => {
          onClose();
        },
        onError: (error) => {
          console.error("Error updating branch:", error);
          alert(`Error updating branch: ${error.message}`);
        },
      });
    }
  };
  // Add this useEffect to validate phone when country or phone changes
  useEffect(() => {
    if (selectedPhone && selectedCountryCode) {
      const country = countries.find(
        (c: any) => c.dial === selectedCountryCode
      );
      if (country && selectedPhone?.length > 0) {
        // Remove any non-digit characters for validation
        const cleanPhone = selectedPhone.replace(/\D/g, "");

        // Check if phone matches the country's regex pattern
        if (!country.regex.test(cleanPhone)) {
          setError("phone", {
            type: "manual",
            message: t("shared.branchForm.invalidPhone"),
          });
        } else {
          // Clear the error if phone is valid
          clearErrors("phone");
        }
      }
    } else if (!selectedPhone) {
      // Clear error if phone is empty
      clearErrors("phone");
    }
  }, [selectedPhone, selectedCountryCode, setError, clearErrors, t]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <FormInput
        label={t("shared.branchForm.branchName")}
        name="branchName"
        placeholder={t("shared.branchForm.branchNamePlaceholder")}
        error={errors.branchName}
        register={register}
      />

      <div className="grid grid-cols-2 gap-4">
        {/* City Selection with Listbox */}
        <div>
          <label className="block font-medium mb-1">
            {t("shared.branchForm.city")}{" "}
            <span className="text-[#0033CC]">*</span>
          </label>
          <Listbox
            value={
              citiesData?.data?.find(
                (city) => String(city.id) === String(watch("city"))
              ) || null
            }
            onChange={(city) => {
              setValue("city", city?.id?.toString() || "");
              clearErrors("city");
            }}
          >
            <div className="relative">
              <Listbox.Button className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-gray-300 bg-white text-gray-500">
                <span className="block truncate text-left">
                  {citiesData?.data?.find(
                    (city) => String(city.id) === String(watch("city"))
                  )?.name || t("shared.branchForm.cityPlaceholder")}
                </span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg
                    className="w-5 h-5 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </Listbox.Button>

              <Listbox.Options className="absolute z-[1000] mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
                {citiesData?.data?.map((city) => (
                  <Listbox.Option
                    key={city.id}
                    value={city}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${
                        active ? "bg-blue-100 text-blue-900" : "text-gray-900"
                      }`
                    }
                  >
                    {({ selected }) => (
                      <>
                        <span
                          className={`block truncate ${selected ? "font-medium" : "font-normal"}`}
                        >
                          {city.name}
                        </span>
                        {selected ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                            <svg
                              className="w-5 h-5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </div>
          </Listbox>
          {errors.city && (
            <span className="text-red-500 text-xs">{errors.city.message}</span>
          )}
        </div>

        {/* District Selection with Listbox */}
        <div>
          <label className="block font-medium mb-1">
            {t("shared.branchForm.district")}
          </label>
          <Listbox
            value={
              areasData?.data?.find(
                (area) => String(area.id) === String(watch("district"))
              ) || null
            }
            onChange={(area) => {
              setValue("district", area?.id?.toString() || "");
              clearErrors("district");
            }}
            disabled={!selectedCity}
          >
            <div className="relative">
              <Listbox.Button
                disabled={!selectedCity}
                className={`w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-gray-300 bg-white text-gray-500 ${!selectedCity ? "bg-gray-100 cursor-not-allowed" : ""}`}
              >
                <span className="block truncate text-left">
                  {areasData?.data?.find(
                    (area) => String(area.id) === String(watch("district"))
                  )?.name || t("shared.branchForm.districtPlaceholder")}
                </span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg
                    className="w-5 h-5 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </Listbox.Button>

              <Listbox.Options className="absolute z-[1000] mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
                {areasData?.data?.map((area) => (
                  <Listbox.Option
                    key={area.id}
                    value={area}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${
                        active ? "bg-blue-100 text-blue-900" : "text-gray-900"
                      }`
                    }
                  >
                    {({ selected }) => (
                      <>
                        <span
                          className={`block truncate ${selected ? "font-medium" : "font-normal"}`}
                        >
                          {area.name}
                        </span>
                        {selected ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                            <svg
                              className="w-5 h-5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </div>
          </Listbox>
          {errors.district && (
            <span className="text-red-500 text-xs">
              {errors.district.message}
            </span>
          )}
        </div>
      </div>

      <FormInput
        label={t("shared.branchForm.address")}
        name="address"
        placeholder={t("shared.branchForm.addressPlaceholder")}
        error={errors.address}
        register={register}
      />

      <div>
        <label className="block font-medium mb-1">
          {t("shared.branchForm.contactFirstName")}{" "}
          <span className="text-[#0033CC]">*</span>
        </label>
        <div className="grid grid-cols-2 gap-4">
          <input
            {...register("contactFirstName")}
            className={`w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-gray-300 ${
              errors.contactFirstName ? "border-red-500" : ""
            }`}
            placeholder={t("shared.branchForm.firstNamePlaceholder")}
          />
          <input
            {...register("contactLastName")}
            className={`w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-gray-300 ${
              errors.contactLastName ? "border-red-500" : ""
            }`}
            placeholder={t("shared.branchForm.lastNamePlaceholder")}
          />
        </div>
        {(errors.contactFirstName || errors.contactLastName) && (
          <span className="text-red-500 text-xs">
            {errors.contactFirstName?.message ||
              errors.contactLastName?.message}
          </span>
        )}
      </div>

      <div>
        <label className="block font-medium mb-1">
          {t("shared.branchForm.contactPhone")}{" "}
        </label>
        <div
          dir="ltr"
          className="flex focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-gray-300 rounded-lg"
        >
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              className={`flex items-center border border-r-0 rounded-l-lg px-3 py-2 bg-white min-w-[60px] focus:outline-none ${
                (errors.country || errors.phone) && !phoneFocused
                  ? "border-red-500"
                  : ""
              }`}
              onClick={() => setShowDropdown((v) => !v)}
              tabIndex={0}
            >
              <span className="text-xl">{selectedCountry.flag}</span>
              <span className="ml-2">
                <img src={sortIcon} alt="Sort" className="w-6 h-6" />
              </span>
            </button>
            {showDropdown && (
              <div
                className="absolute left-0 bottom-full mb-1 bg-white border rounded-lg shadow-lg z-10"
                style={{ minWidth: "100%", width: "max-content" }}
              >
                {countries.map((country: any) => (
                  <div
                    key={country.code}
                    className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 ${
                      selectedCountry.dial === country.dial ? "bg-gray-100" : "" // Compare by dial
                    }`}
                    onClick={() => onSelectCountry(country.dial)}
                  >
                    <span className="mr-2 text-xl">{country.flag}</span>
                    <span className="mr-2 text-sm text-gray-700">
                      {t(`shared.branchForm.countries.${country.code}`)}
                    </span>
                    <span className="ml-auto text-gray-500 text-xs">
                      {country.dial}
                    </span>
                    {selectedCountry.dial === country.dial && ( // Compare by dial
                      <span className="ml-6 flex items-center">
                        <CheckIcon />
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <input
            dir={isRTL ? "rtl" : "ltr"}
            {...register("phone")}
            className={`w-full border-t border-b border-r rounded-r-lg px-4 py-2 focus:outline-none ${
              (errors.phone || errors.country) && !phoneFocused
                ? "border-red-500"
                : ""
            } hideArrowsInput`}
            placeholder={t("shared.branchForm.phonePlaceholder")}
            type="tel"
            onFocus={() => setPhoneFocused(true)}
            onBlur={(e: any) => {
              setPhoneFocused(false);
              // Trigger the custom validation logic on blur
              const phoneValue = e.target.value;
              const countryData = countries.find(
                (c) => c.dial === selectedCountry.dial
              );

              if (
                countryData &&
                phoneValue &&
                !countryData.regex.test(phoneValue)
              ) {
                // Set custom error for phone validation
                setError("phone", {
                  type: "manual",
                  message: "Invalid phone number format for selected country",
                });
              } else {
                // Clear phone error if validation passes
                clearErrors("phone");
              }
            }}
          />
        </div>
        {errors.phone && (
          <span className="text-red-500 text-xs">{errors.phone.message}</span>
        )}
        {errors.country && (
          <span className="text-red-500 text-xs">{errors.country.message}</span>
        )}
      </div>
      <FormInput
        label={t("shared.branchForm.contactEmail")}
        name="contactEmail"
        placeholder={t("shared.branchForm.emailPlaceholder")}
        error={errors.contactEmail}
        register={register}
        required={false}
        type="email"
        icon={<img src={mailIcon} alt="Mail" className="w-5 h-5" />}
      />
      <div className="flex gap-4" dir="ltr">
        <Button
          variant="outline"
          onClick={onClose}
          className="w-full text-[16px]"
          size="lg"
        >
          {t("back")}
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={
            isSubmitting ||
            createBranchMutation.isPending ||
            updateBranchMutation.isPending ||
            createTenantBranchMutation.isPending
          }
          className="w-full text-[16px]"
        >
          {createBranch || createTenantBranch ? "Add" : "Next"}
        </Button>
      </div>
    </form>
  );
};

export default AssignBranchForm;
